import React, { useState, useEffect } from 'react';
import {
  Table, Tag, Button, Select, Modal, Input, message, Space, Upload, Image, Empty, Alert,
} from 'antd';
import {
  CheckCircleOutlined, CloseCircleOutlined, PictureOutlined, PlusOutlined, LoadingOutlined, DeleteOutlined, StopOutlined, EditOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import {
  useListAllDealsQuery,
  usePublishDealMutation,
  useExpireDealMutation,
  useRejectDealMutation,
  uploadImage,
  updateDealImages,
  fetchDealDetail,
} from '../services/adminApi';
import AdminPageHeader, { adminPanelStyle } from './AdminPageHeader';

const { TextArea } = Input;

const STATUS_COLORS = {
  draft: 'default',
  pending_review: 'orange',
  published: 'green',
  rejected: 'red',
  expired: 'default',
  archived: 'default',
  removed: 'red',
};

const STATUS_FILTERS = [
  { value: '', label: 'All statuses' },
  { value: 'pending_review', label: 'Pending Review' },
  { value: 'published', label: 'Published' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'expired', label: 'Expired' },
  { value: 'draft', label: 'Draft' },
];

function ImagesModal({ open, dealPublicId, onClose }) {
  const [deal, setDeal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [images, setImages] = useState([]);

  useEffect(() => {
    if (open && dealPublicId) {
      setLoading(true);
      fetchDealDetail(dealPublicId)
        .then((d) => {
          setDeal(d);
          setImages(d.images || []);
        })
        .catch(() => message.error('Failed to load deal'))
        .finally(() => setLoading(false));
    }
  }, [open, dealPublicId]);

  const handleUpload = async (file) => {
    setUploading(true);
    try {
      const result = await uploadImage(file);
      setImages((prev) => [...prev, { id: Date.now(), url: result.url, sortOrder: prev.length }]);
      return false;
    } catch (err) {
      message.error(err.message || 'Upload failed');
      return Upload.LIST_IGNORE;
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = (urlToRemove) => {
    setImages((prev) => prev.filter((img) => img.url !== urlToRemove));
  };

  const handleSave = async () => {
    if (images.length > 5) {
      message.warning('Maximum 5 images allowed');
      return;
    }
    setSaving(true);
    try {
      await updateDealImages(dealPublicId, images.map((img) => img.url));
      message.success('Images updated');
      onClose();
    } catch (err) {
      message.error(err.message || 'Failed to update images');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={`Manage Images — ${deal?.title?.substring(0, 50) || ''}${deal?.title?.length > 50 ? '...' : ''}`}
      open={open}
      onCancel={onClose}
      onOk={handleSave}
      confirmLoading={saving}
      okText="Save Images"
      width={640}
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}><LoadingOutlined style={{ fontSize: 28 }} /></div>
      ) : (
        <>
          <div style={{ marginBottom: 16 }}>
            <Upload
              listType="picture-card"
              showUploadList={false}
              beforeUpload={handleUpload}
              accept=".jpg,.jpeg,.png,.webp"
            >
              {images.length >= 5 ? null : (
                <div>
                  {uploading ? <LoadingOutlined /> : <PlusOutlined />}
                  <div style={{ marginTop: 8, fontSize: 12 }}>Upload</div>
                </div>
              )}
            </Upload>
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
            {images.length === 0 && (
              <span style={{ color: 'var(--df-text-secondary)' }}>No images. Upload some above.</span>
            )}
            {images.map((img) => (
              <div
                key={img.id || img.url}
                style={{
                  width: 100,
                  height: 100,
                  borderRadius: 8,
                  border: '1px solid var(--df-border)',
                  overflow: 'hidden',
                  position: 'relative',
                }}
              >
                <Image
                  src={`${img.url}?tr=w-200,h-200,fo-auto`}
                  alt=""
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  preview={{ src: img.url }}
                />
                <button
                  type="button"
                  aria-label="Remove image"
                  onClick={() => handleRemove(img.url)}
                  style={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    width: 22,
                    height: 22,
                    borderRadius: '50%',
                    background: 'rgba(0,0,0,0.5)',
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    fontSize: 12,
                    border: 'none',
                  }}
                >
                  <DeleteOutlined />
                </button>
              </div>
            ))}
          </div>

          <div style={{ marginTop: 12, fontSize: 13, color: images.length >= 3 ? 'var(--df-success)' : 'var(--df-warning)' }}>
            {images.length} of 5 images
          </div>
        </>
      )}
    </Modal>
  );
}

export default function DealsPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState('pending_review');
  const [page, setPage] = useState({ current: 1, pageSize: 25 });
  const offset = (page.current - 1) * page.pageSize;
  const { data, isLoading, isError, error } = useListAllDealsQuery({
    status: status || undefined,
    limit: page.pageSize,
    offset,
  });
  const [publishDeal] = usePublishDealMutation();
  const [expireDeal] = useExpireDealMutation();
  const [rejectDeal] = useRejectDealMutation();
  const [publishModal, setPublishModal] = useState(null);
  const [rejectModal, setRejectModal] = useState(null);
  const [acting, setActing] = useState(false);
  const [imagesModal, setImagesModal] = useState(null);

  const handlePublish = async () => {
    setActing(true);
    try {
      await publishDeal({ publicId: publishModal.publicId, moderationNote: publishModal.note }).unwrap();
      message.success('Deal published');
      setPublishModal(null);
    } catch (err) {
      message.error(err.data?.title || 'Failed to publish');
    } finally {
      setActing(false);
    }
  };

  const handleReject = async () => {
    setActing(true);
    try {
      await rejectDeal({ publicId: rejectModal.publicId, reason: rejectModal.reason }).unwrap();
      message.success('Deal rejected');
      setRejectModal(null);
    } catch (err) {
      message.error(err.data?.title || 'Failed to reject');
    } finally {
      setActing(false);
    }
  };

  const handleExpire = async (publicId) => {
    try {
      await expireDeal(publicId).unwrap();
      message.success('Deal expired');
    } catch (err) {
      message.error(err.data?.title || 'Failed to expire');
    }
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
    {
      title: 'Image',
      dataIndex: 'primary_image_url',
      key: 'image',
      width: 60,
      render: (url) => (url ? (
        <Image
          src={`${url}?tr=w-48,h-48,fo-auto`}
          alt=""
          style={{ width: 40, height: 40, borderRadius: 6, objectFit: 'cover' }}
          preview={false}
        />
      ) : (
        <div style={{
          width: 40, height: 40, borderRadius: 6, background: 'var(--df-border-subtle)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, color: 'var(--df-text-muted)',
        }}
        >
          N/A
        </div>
      )),
    },
    { title: 'Title', dataIndex: 'title', key: 'title', ellipsis: true },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 130,
      render: (s) => <Tag color={STATUS_COLORS[s]}>{s?.replace('_', ' ')}</Tag>,
    },
    {
      title: 'Price',
      dataIndex: 'price_minor',
      key: 'price',
      width: 100,
      render: (p) => (p != null ? `$${(p / 100).toFixed(2)}` : '—'),
    },
    { title: 'Merchant', dataIndex: 'merchant_name', key: 'merchant_name', width: 130 },
    {
      title: 'Submitted',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 170,
      render: (d) => (d ? new Date(d).toLocaleString() : '—'),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 380,
      render: (_, record) => (
        <Space wrap>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => navigate(`/admin/deals/${record.public_id}/edit`)}
          >
            Edit
          </Button>
          <Button
            size="small"
            icon={<PictureOutlined />}
            onClick={() => setImagesModal(record.public_id)}
          >
            Images
          </Button>
          {record.status === 'pending_review' && (
            <>
              <Button
                size="small"
                type="primary"
                icon={<CheckCircleOutlined />}
                onClick={() => setPublishModal({ publicId: record.public_id, note: '' })}
              >
                Publish
              </Button>
              <Button
                size="small"
                danger
                icon={<StopOutlined />}
                onClick={() => setRejectModal({ publicId: record.public_id, reason: '' })}
              >
                Reject
              </Button>
            </>
          )}
          {record.status === 'published' && (
            <Button
              size="small"
              danger
              icon={<CloseCircleOutlined />}
              onClick={() => handleExpire(record.public_id)}
            >
              Expire
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <AdminPageHeader
        title="Deals"
        subtitle="Approve pending submissions, edit deal details, manage images, expire live deals"
        extra={(
          <Select
            value={status}
            onChange={(v) => {
              setStatus(v || '');
              setPage((p) => ({ ...p, current: 1 }));
            }}
            options={STATUS_FILTERS}
            style={{ width: 180 }}
          />
        )}
      />

      {isError && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={error?.data?.title || 'Failed to load deals'}
        />
      )}

      <div style={adminPanelStyle}>
        <Table
          dataSource={data?.items}
          columns={columns}
          loading={isLoading}
          rowKey="id"
          pagination={{
            current: page.current,
            pageSize: page.pageSize,
            total: data?.page?.total || 0,
            showSizeChanger: true,
            pageSizeOptions: ['25', '50', '100'],
            onChange: (current, pageSize) => setPage({ current, pageSize }),
          }}
          size="middle"
          locale={{ emptyText: <Empty description="No deals for this filter" /> }}
        />
      </div>

      <Modal
        title="Publish Deal"
        open={!!publishModal}
        onOk={handlePublish}
        onCancel={() => setPublishModal(null)}
        confirmLoading={acting}
        okText="Publish"
      >
        <p style={{ color: 'var(--df-text-secondary)', marginBottom: 12 }}>
          This deal will be published and visible to all users.
        </p>
        <TextArea
          placeholder="Moderation note (optional)"
          rows={3}
          value={publishModal?.note || ''}
          onChange={(e) => setPublishModal({ ...publishModal, note: e.target.value })}
        />
      </Modal>

      <Modal
        title="Reject Deal"
        open={!!rejectModal}
        onOk={handleReject}
        onCancel={() => setRejectModal(null)}
        confirmLoading={acting}
        okText="Reject"
        okButtonProps={{ danger: true }}
      >
        <p style={{ color: 'var(--df-text-secondary)', marginBottom: 12 }}>
          The submitter will see this deal as rejected.
        </p>
        <TextArea
          placeholder="Rejection reason (optional)"
          rows={3}
          value={rejectModal?.reason || ''}
          onChange={(e) => setRejectModal({ ...rejectModal, reason: e.target.value })}
        />
      </Modal>

      <ImagesModal
        open={!!imagesModal}
        dealPublicId={imagesModal}
        onClose={() => setImagesModal(null)}
      />
    </div>
  );
}
